import { ContractType } from '@app/features/contracts/contract.model';
import { CdTDocument, DocumentTag } from '@app/features/documents/document.model';
import moment from 'moment';

/** Ce qu'il faut savoir d'un contrat pour le nommer. */
export interface ContractNaming {
  contractType: ContractType | null;
  startDate: string | null;
  endDate: string | null;
}

/** Pièces déposées qui se téléchargent sous le nom du contrat, et non sous celui des pièces. */
const contractDocumentTags: DocumentTag[] = ['contract', 'signedContract'];

function formatDate(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = moment(value);
  return date.isValid() ? date.format('DD.MM.YYYY') : null;
}

function period(contract: ContractNaming): string | null {
  const start = formatDate(contract.startDate);
  if (!start) {
    return null;
  }
  const end = contract.contractType === 'CDI' ? null : formatDate(contract.endDate);
  return end ? `${start} au ${end}` : `à partir de ${start}`;
}

/**
 * Nom, sans extension, sous lequel un contrat se télécharge : celui que le pôle social lui donne
 * sur son disque réseau (RG-contrat-nom-de-telechargement). Son type, sa période — `à partir de`
 * sans date de fin —, `signé` pour un contrat signé, puis un numéro quand plusieurs pièces de
 * même nature le portent. Ce qui n'est pas saisi est omis ; un nom auquel il ne reste rien
 * commence par `Contrat`.
 */
export function contractDownloadName(
  contract: ContractNaming | null | undefined,
  options: { signed?: boolean; number?: number } = {},
): string {
  const parts = [contract?.contractType ?? null, contract ? period(contract) : null].filter(
    (part): part is string => !!part,
  );
  if (parts.length === 0) {
    parts.push('Contrat');
  }
  if (options.signed) {
    parts.push('signé');
  }
  if (options.number) {
    parts.push(String(options.number));
  }
  return parts.join(' ');
}

/**
 * Donne à chaque pièce « Contrat de travail » et « Contrat signé » le nom sous lequel elle se
 * télécharge, numéroté de la plus ancienne à la plus récente quand plusieurs pièces sont de même
 * nature. Le nom affiché et l'ordre des pièces ne changent pas, ni les autres pièces.
 */
export function nameContractDownloads(
  documents: CdTDocument[],
  contract: ContractNaming | null | undefined,
): void {
  for (const tag of contractDocumentTags) {
    const sameNature = documents
      .filter((document) => document.tag === tag)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    sameNature.forEach((document, index) => {
      document.downloadName = contractDownloadName(contract, {
        signed: tag === 'signedContract',
        number: sameNature.length > 1 ? index + 1 : undefined,
      });
    });
  }
}
